import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ElementConditionLogicSocketService } from '@service/element-condition-logic-socket.service';
import { ElementConditionSocketService } from '@service/element-condition-socket.service';
import { MatrixChannelSocketService } from '@service/matrix-channel-socket.service';
import { MatrixChannelService } from '@service/matrix-channel.service';
import { ChannelFns } from 'icreate/common/models/channel';
import { ElementConditionViewData } from 'icreate/common/models/element-condition';
import { ALARM_OPTION, ElementConditionLogic } from 'icreate/common/models/element-condition-logic';
import { MatrixChannelViewData } from 'icreate/common/models/matrix-channel';
import { Vessel } from 'icreate/common/models/vessel';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-trigger-condition-logic-dialog',
    templateUrl: './trigger-condition-logic-dialog.component.html',
    styleUrls: ['./trigger-condition-logic-dialog.component.css'],
    standalone: false,
})
export class TriggerConditionLogicDialogComponent implements OnInit, OnDestroy {
    readonly ALARM_OPTION = ALARM_OPTION;

    isLoading: boolean = false;
    matrixChannelList: MatrixChannelViewData[] = [];
    conditionLogitList: ElementConditionLogic[] = [];

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            vessel: Vessel;
            elementId: string;
            condition: ElementConditionViewData;
        },
        private matrixChannelService: MatrixChannelService,
        private dialogRef: MatDialogRef<TriggerConditionLogicDialogComponent>,
        private matrixChannelSocketService: MatrixChannelSocketService,
        private elementConditionSocketService: ElementConditionSocketService,
        private elementConditionLogicSocketService: ElementConditionLogicSocketService
    ) {}

    async ngOnInit() {
        try {
            this.isLoading = true;

            if (!this.data.elementId) throw new Error('Element ID is required');

            const matrixChannelList = await this.matrixChannelService.getByElementId(this.data.elementId);

            this.matrixChannelList = matrixChannelList;
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }

        this.elementConditionLogicSocketService
            .ElementConditionLogicUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((event: { logicList: ElementConditionLogic[]; elementId: string }) => {
                const { logicList, elementId } = event;
                if (this.data.elementId === elementId) {
                    this.matrixChannelList = this.matrixChannelList.map(matrixChannel => ({
                        ...matrixChannel,
                        logicList: [],
                    }));
                    logicList.forEach((logic: ElementConditionLogic) => {
                        const updateToIndex = this.matrixChannelList.findIndex(
                            matrixChannel => matrixChannel.id === logic.matrixChannelId
                        );
                        if (updateToIndex !== -1) {
                            this.matrixChannelList[updateToIndex].logicList.push({
                                ...logic,
                                channel: { ...this.matrixChannelList[updateToIndex].channel },
                                isError: false,
                            });
                        }
                    });
                }
            });

        this.matrixChannelSocketService
            .matrixChannelUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((event: { matrixChannelList: MatrixChannelViewData[]; elementId: string }) => {
                const { matrixChannelList, elementId } = event;

                if (this.data.elementId === elementId) {
                    this.matrixChannelList = matrixChannelList.map(matrixChannel => ({
                        ...matrixChannel,
                        logicList: matrixChannel.logicList?.map(logicList => ({ ...logicList })) ?? [],
                    }));
                }
            });

        this.elementConditionSocketService
            .elementConditionUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((event: { conditionList: ElementConditionViewData[]; elementId: string }) => {
                const { conditionList, elementId } = event;
                const matchedCondition = conditionList.find(condition => this.data.condition.id === condition.id);
                if (this.data.elementId === elementId && !matchedCondition) {
                    this.close(undefined);
                }
            });
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    getLogicDataToText(matrixChannel: MatrixChannelViewData) {
        const index = this.data.condition?.index;
        if (index === undefined) return '';
        const currentLogic = matrixChannel?.logicList[index];
        return currentLogic.setToAlarm
            ? this.ALARM_OPTION
            : (!this.isDigitalChannel(matrixChannel)
                  ? (currentLogic?.analogOperator ? currentLogic?.analogOperator : '') +
                    (currentLogic?.analogValue ? currentLogic?.analogValue : '')
                  : currentLogic?.digitalString
                    ? currentLogic?.digitalString
                    : '') +
                  ' ' +
                  (matrixChannel?.channel?.opsData?.measurementUnit
                      ? matrixChannel?.channel?.opsData?.measurementUnit
                      : '');
    }

    isDigitalChannel(matrixChannel: MatrixChannelViewData) {
        return ChannelFns.isDigital(matrixChannel.channel);
    }

    getCurrentConditionLogic(logicList: ElementConditionLogic[]) {
        return logicList[this.data.condition?.index];
    }

    close(result) {
        this.dialogRef.close(result);
    }
}
